import React, { useState } from 'react';
import Switch from 'react-switch';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { setAutoPubNf } from '../userSlice';
import Spinner from '../spinner/Spinner';
import { selectAutoPublish, selectUserId } from '../selectors/userSelectors';

const AutoPubSwitch = ({
    updateSelectedPublications,
    newStyle = false,
}: {
    updateSelectedPublications: () => void;
    newStyle?: boolean;
}) => {
    const dispatch = useDispatch();
    const autoPubliNf = useSelector(selectAutoPublish);
    const userId = useSelector(selectUserId);
    const [isChecked, setIsChecked] = useState(autoPubliNf);
    const [loading, setLoading] = useState(false);

    const handleChange = async (checked: boolean) => {
        setIsChecked(checked);
        setLoading(true);

        try {
            if (userId !== undefined) {
                await axios.patch(
                    `/api/users/${userId}`,
                    { autoPublish: checked },
                    { headers: { 'Content-Type': 'application/merge-patch+json' } },
                );

                dispatch(setAutoPubNf(checked));
            }

            if (!checked) {
                await disableAutoPubForUser();
            } else {
                await enableAutoPubForUser();
            }

            updateSelectedPublications();
        } catch (error) {
            console.error('Erreur lors de la mise à jour :', error);
        } finally {
            setLoading(false);
        }
    };

    const disableAutoPubForUser = async () => {
        try {
            const response = await axios.get(
                `/api/publications?status=1&pagination=false&isGlobal=true`,
            );
            const globalPublications = response.data['hydra:member'];
            const alreadyUnpublished = await axios.get(
                `/api/user_unpublishes?user=${userId}&pagination=false`,
            );
            const unpublishedIds = alreadyUnpublished.data['hydra:member'].map((item: any) => {
                const parts = item.publication.split('/');
                return parseInt(parts[parts.length - 1], 10);
            });

            const newPublications = globalPublications.filter(
                (pub: any) => !unpublishedIds.includes(pub.id),
            );
            const requests = newPublications.map((pub: any) =>
                axios.post(
                    '/api/user_unpublishes',
                    {
                        user: `/api/users/${userId}`,
                        publication: `/api/publications/${pub.id}`,
                    },
                    {
                        headers: { 'Content-Type': 'application/ld+json' },
                    },
                ),
            );
            await Promise.all(requests);
            console.log('Toutes les publications globales ont été désactivées.');
        } catch (error) {
            console.error('Erreur lors de la désactivation des publications globales :', error);
        }
    };

    const enableAutoPubForUser = async () => {
        try {
            const response = await axios.get(
                `/api/user_unpublishes?user=${userId}&pagination=false`,
            );
            const unpublishEntries = response.data['hydra:member'];

            const deleteRequests = unpublishEntries
                .filter((entry: any) => entry.publication.includes('/api/publications/'))
                .map((entry: any) => axios.delete(`/api/user_unpublishes/${entry.id}`));

            await Promise.all(deleteRequests);
            console.log('Toutes les publications globales ont été réactivées.');
        } catch (error) {
            console.error("Erreur lors de l'activation des publications globales :", error);
        }
    };

    return (
        <div
            style={{
                flex: '0 0 calc(50% - 12px)',
                padding: 20,
                background: 'white',
                borderRadius: 8,
                outline: '1px #E5E5E5 solid',
                outlineOffset: '-1px',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 12,
                display: 'flex',
                marginTop: 32,
            }}
        >
            {loading ? (
                <Spinner loading={loading} />
            ) : (
                <Switch
                    onChange={handleChange}
                    checked={isChecked}
                    uncheckedIcon={false}
                    checkedIcon={false}
                    onColor="#bed000"
                    offColor="#ccc"
                    height={16}
                    width={28}
                    handleDiameter={12}
                />
            )}
            <div
                style={{
                    flex: '1 1 0',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                    gap: 12,
                    display: 'inline-flex',
                }}
            >
                <div
                    style={{
                        alignSelf: 'stretch',
                        justifyContent: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        color: '#393939',
                        fontSize: 14,
                        fontFamily: 'Montserrat',
                        fontWeight: '400',
                        lineHeight: '18px',
                        wordWrap: 'break-word',
                    }}
                >
                    Publier de manière automatique les publications de News Factory
                </div>
            </div>
        </div>
    );
};

export default AutoPubSwitch;
